import { Params } from '@angular/router';
import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { switchMap, filter, debounceTime, map } from 'rxjs/operators';
import { Observable, BehaviorSubject, Subject, of } from 'rxjs';
import { ITitleSegment } from '@core-lib/models';

@Injectable({
	providedIn: 'root'
})
export class PageService {
	private static readonly BROWSER_TITLE_MAX_LENGTH = 50;

	private pageTitleParams$ = new BehaviorSubject<ITitleSegment>({
		key: '',
		hasTranslationKey: false
	});

	private pageSubTitleParams$ = new BehaviorSubject<ITitleSegment>({
		key: '',
		hasTranslationKey: false
	});
	
	private pageTitleAffaireNameParams$ = new BehaviorSubject<ITitleSegment>({
		key: '',
		hasTranslationKey: false
	});

	private browserTitleParams$ = new Subject<any>();

	// the optionaly "to be translated" browser title ; tant que la langue n'est pas chargee, la cle brute ne doit pas sortir
	private browserTitle$: Observable<[string, boolean]> = this.browserTitleParams$.pipe(
		filter(p => (!!p.key || !p.hasTranslationKey) && !!this.translateService.currentLang),
		switchMap(p => this.translateIfNeeded(p)),
		debounceTime(100) // smoother flashes (page-with-tabs, etc..)
	);

	// the optionaly "to be translated" page title
	public pageTitle$: Observable<[string, boolean]> = this.pageTitleParams$.pipe(
		filter(p => !!p.key || !p.hasTranslationKey),
		switchMap(p => this.translateIfNeeded(p)),
		debounceTime(100) // smoother flashes (page-with-tabs, etc..)
	);

	// the optionaly "to be translated" page subTitle
	public pageSubTitle$: Observable<[string, boolean]> = this.pageSubTitleParams$.pipe(
		filter(p => !!p.key || !p.hasTranslationKey),
		switchMap(p => this.translateIfNeeded(p)),
		debounceTime(100) // smoother flashes (page-with-tabs, etc..)
	);
	
	public pageTitleAffaireName$: Observable<[string, boolean]> = this.pageTitleAffaireNameParams$.pipe(
		filter(p => !!p.key || !p.hasTranslationKey),
		switchMap(p => this.translateIfNeeded(p)),
		debounceTime(100) // smoother flashes (page-with-tabs, etc..)
	);

	constructor(private titleService: Title, private translateService: TranslateService) {
		this.browserTitle$.subscribe(title => {
			this.titleService.setTitle(this.truncateBrowserTitle(title[0]));
		});
	}

	// un titre borne garde l'identifiant (numero d'affaire, de compte) lisible dans un onglet etroit
	private truncateBrowserTitle(title: string): string {
		const max = PageService.BROWSER_TITLE_MAX_LENGTH;
		return title.length > max ? `${title.substring(0, max).trimEnd()}...` : title;
	}

	private translateIfNeeded(segment: ITitleSegment): Observable<[string, boolean]> {
		let hasSpecialStyle = segment.hasSpecialStyle ? segment.hasSpecialStyle : false;
		if (segment.hasTranslationKey) {
			return this.translateService
				.get(`PageTitles.${segment.key}`, {
					data: segment.params
				})
				.pipe(map((translation: string) => [translation, hasSpecialStyle]));
		} else {
			return of([segment.key, hasSpecialStyle]);
		}
	}

	setPageTitle(segment: ITitleSegment): void {
		this.pageTitleParams$.next(segment);
	}

	setPageSubTitle(segment: ITitleSegment): void {
		this.pageSubTitleParams$.next(segment);
	}
	
	setPageAffaireName(segment: ITitleSegment): void {
		this.pageTitleAffaireNameParams$.next(segment);
	}

	setBrowserTitle(segment: ITitleSegment): void {
		this.browserTitleParams$.next(segment);
	}

	setTitle(pageTitleKey: string, pageTitleParams?: Params, browserTitleKey?: string, browserTitleParams?: Params): void {
		this.setPageTitle({
			key: pageTitleKey,
			params: pageTitleParams,
			hasTranslationKey: true
		});

		if (!browserTitleKey) {
			this.setBrowserTitle({
				key: pageTitleKey,
				params: pageTitleParams,
				hasTranslationKey: true
			});
		} else {
			this.setBrowserTitle({
				key: browserTitleKey,
				params: browserTitleParams,
				hasTranslationKey: true
			});
		}
	}
}
